<!--
� 2025, NTT Data Group. All rights reserved. See LICENSE.txt file in the project root for third party licenses info.
-->

<section class="page">
	<section class="summary">
		<div class="filters">
			<div class="period">
				<globalictcc-dropdown
					label="Reporting period"
					[formControl]="periodControl"
					[options]="periodOptions"
				/>

				<globalictcc-button
					label="Apply"
					(handleClick)="onApply()"
					[disabled]="!periodControl.value"
				/>
			</div>
		</div>

		@let canSeeTotalCo2 = authorizationService.canSee(grants.totalCo2Emissions);
		@let canSeeByDomain = authorizationService.canSee(grants.totalEmissionsBreakdownByDomain);
		@let canSeeByScope = authorizationService.canSee(grants.totalEmissionsBreakdownByScope);

		@if (canSeeTotalCo2 || canSeeByDomain || canSeeByScope) {
			<span class="header">Summary</span>

			<div class="summary-grid">
				@if (canSeeTotalCo2) {
					@let summary = summary$ | async;

					<div class="total-co2">
						<globalictcc-kpi
							label="Total CO2e Emissions"
							[value]="summary?.data?.totalEmissions"
							unit="tCO2e"
						/>
					</div>
				}

				<div class="summary-content">
					@if (canSeeByDomain || canSeeByScope) {
						<div class="summary-chart-panel">
							<div class="summary-chart">
								@if (canSeeByDomain) {
									@let byType = emissionsShareByType$ | async;

									<globalictcc-chart
										#chartByType
										chartType="pie"
										titleSize="small"
										[data]="byType?.data?.chart"
										[isLoading]="byType?.isLoading ?? true"
										[hasError]="byType?.hasError || byType?.data?.hasError"
										[colors]="emissionsSharePieChartColorsByType"
										height="280px"
										(handleChartRefresh)="handleEmissionsShareByTypeRefresh()"
									/>
								}
								@if (canSeeByScope) {
									@let byScope = emissionsShareByScope$ | async;

									<globalictcc-chart
										#chartByScope
										chartType="pie"
										titleSize="small"
										[data]="byScope?.data?.chart"
										[isLoading]="byScope?.isLoading ?? true"
										[hasError]="byScope?.hasError || byScope?.data?.hasError"
										[colors]="emissionsSharePieChartColorsByScope"
										height="280px"
										(handleChartRefresh)="handleEmissionsShareByScopeRefresh()"
									/>
								}
							</div>

							<div class="summary-chart-actions">
								@if (canSeeByDomain) {
									<globalictcc-button
										label="Export by type"
										[textOnly]="true"
										iconName="download"
										(handleClick)="
											onExportChart(
												emissionsShareByTypeChart?.instance,
												'total_emission_by_type'
											)
										"
									/>
								}

								@if (canSeeByScope) {
									<globalictcc-button
										label="Export by scope"
										[textOnly]="true"
										iconName="download"
										(handleClick)="
											onExportChart(
												emissionsShareByScopeChart?.instance,
												'total_emission_by_scope'
											)
										"
									/>
								}
							</div>
						</div>
					}

					<!-- These cards come from the same API as the Total CO2e KPI and have no grant of their own. -->
					@if (canSeeTotalCo2) {
						@let summary = summary$ | async;

						<div class="summary-side">
							<div class="summary-title">
								<span class="title">This is equivalent to...</span>
							</div>

							<div class="summary-cards">
								<div class="summary-card">
									<span class="icon-home"></span>
									<span class="card-title"
										>{{
											summary?.data?.houses || 0 | maxDecimals: 2
										}}
										Houses</span
									>
									<span class="card-description">
										Equivalent to CO2 emissions generated by
										{{ summary?.data?.houses || 0 | maxDecimals: 2 }} houses
										over the same period
									</span>
								</div>
								<div class="summary-card">
									<span class="icon-car"></span>
									<span class="card-title"
										>{{
											summary?.data?.kmCarDrive || 0 | maxDecimals: 2
										}}
										Cars</span
									>
									<span class="card-description">
										Equivalent to CO2 emissions generated by driving
										{{ summary?.data?.kmCarDrive || 0 | maxDecimals: 2 }} cars
										from London to Paris
									</span>
								</div>
							</div>
						</div>
					}
				</div>
			</div>
		}
	</section>

	@if (authorizationService.canSee(grants.trend)) {
		@let forecast = forecast$ | async;

		<section class="trend">
			<span class="header">Trend</span>

			<div class="charts">
				<div class="chart-panel">
					<globalictcc-chart
						#forecastChart
						chartType="mixed"
						titleSize="small"
						[isLegendVisible]="true"
						[data]="forecast?.data?.chart"
						[isLoading]="forecast?.isLoading ?? true"
						[hasError]="forecast?.hasError || forecast?.data?.hasError"
						[colors]="forecastMixedChartColors"
						[configOverrides]="forecastMixedChartOverrides"
						[patterns]="forecastMixedChartPatterns"
						(handleChartRefresh)="handleForecastChartRefresh()"
					/>

					<div class="chart-actions">
						<globalictcc-button
							label="Export chart"
							[textOnly]="true"
							iconName="download"
							(handleClick)="
								onExportChart(forecastChart.instance, 'total_emission_trend')
							"
						/>
					</div>
				</div>
			</div>
		</section>
	}

	<!--
	<section class="data-centers-overview">
		<span class="header">Data Center Overview</span>
		<div class="overview-grid">
			@for (card of dataCentersOverviewCards; track card.key) {
				<div class="overview-card">
					<globalictcc-kpi
						[label]="card.label"
						[value]="card.value ?? 0"
						[unit]="card.unit"
					/>
				</div>
			}
		</div>
	</section>
	-->
	@if (authorizationService.canSee(grants.detail)) {
		@let detail = detailCharts$ | async;
		@let detailCharts = orderDetailCharts(detail?.data ?? []);

		<section class="detail">
			<span class="header">Detail</span>

			<div class="chips">
				<globalictcc-chips
					[chips]="chips"
					(handleSelection)="handleSelection($event)"
				/>
			</div>

			<div class="charts-detail">
				@if (detailCharts.length) {
					<globalictcc-chart
						chartType="barX"
						[isTitleVisible]="false"
						[isLegendVisible]="true"
						titleAlignment="center"
						titleSize="small"
						[data]="detailCharts[0].chart"
						[isLoading]="detail?.isLoading ?? true"
						[configOverrides]="detailCharts[0].chartOverrides"
						[colors]="detailCharts[0].colors"
						[patterns]="detailChartsByScopePatterns"
						tooltipLabel="serie"
					/>

					<div class="secondary-charts">
						@for (detailChart of detailCharts; track $index) {
							@if ($index > 0) {
								<globalictcc-chart
									chartType="barX"
									[isTitleVisible]="$index !== 0"
									titleAlignment="center"
									titleSize="small"
									[data]="detailChart.chart"
									[isLoading]="detail?.isLoading ?? true"
									[configOverrides]="detailChart.chartOverrides"
									[colors]="detailChart.colors"
									[patterns]="detailChartsByScopePatterns"
									tooltipLabel="serie"
								/>
							}
						}
					</div>
				} @else if (detail?.isLoading ?? true) {
					<globalictcc-chart
						chartType="barX"
						[isLoading]="true"
					/>
				} @else {
					<globalictcc-chart
						chartType="barX"
						[hasError]="true"
						(handleChartRefresh)="handleDetailChartsRefresh()"
					/>
				}
			</div>
		</section>
	}

	<div class="bottom-actions">
		<globalictcc-button
			label="Export report"
			iconName="document"
			[textOnly]="true"
			(handleClick)="onExportReport()"
		/>
		<globalictcc-button
			label="Download as CSV"
			iconName="download"
			[textOnly]="true"
			(handleClick)="onDownloadCsv()"
		/>
	</div>
</section>
